/**
 * Mockup preview de "100 Canales Dijeron" — Family Feud style.
 * Público (bypassa gate/auth) para que Tony pueda ver cómo quedaría
 * antes de decidir qué modo construir primero.
 */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Trophy, X, Clock, Users, Zap, Store, Volume2 } from "lucide-react";

import { CanalesLogo } from "@/components/CanalesLogo";
import { Carlos, Ana, Mascot } from "@/components/characters/Characters";

const BOARD_ANSWERS = [
  { rank: 1, text: "ESTÁ MUY CARO", points: 42, revealed: true },
  { rank: 2, text: "NOMÁS ESTOY VIENDO", points: 28, revealed: true },
  { rank: 3, text: "NO TENGO ESPACIO", points: 16, revealed: false },
  { rank: 4, text: "LO CONSULTO CON MI PAREJA", points: 9, revealed: true },
  { rank: 5, text: "SIN CRÉDITO / TRABAJO", points: 5, revealed: false },
];

function BoardSlot({ answer, index }) {
  const isRevealed = answer.revealed;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      className={`brutal ${isRevealed ? "bg-canales-cream text-black" : "bg-canales-blue text-white"} flex items-center justify-between px-4 py-3`}
      style={{ minHeight: "72px" }}
    >
      {isRevealed ? (
        <>
          <div className="flex items-center gap-3">
            <span className="font-display font-black text-3xl text-canales-red">
              #{answer.rank}
            </span>
            <span className="font-display font-black text-lg md:text-xl tracking-wide">
              {answer.text}
            </span>
          </div>
          <div className="brutal-sm bg-canales-red text-white px-3 py-1 font-display font-black text-2xl">
            {answer.points}
          </div>
        </>
      ) : (
        <div className="w-full flex items-center justify-center">
          <span className="font-display font-black text-5xl text-canales-yellow">
            {answer.rank}
          </span>
        </div>
      )}
    </motion.div>
  );
}

function StrikeIndicator({ count }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className={`w-10 h-10 brutal-sm flex items-center justify-center font-display font-black text-2xl ${
            n <= count ? "bg-canales-red text-white" : "bg-white text-neutral-200"
          }`}
        >
          <X className="w-6 h-6" strokeWidth={4} />
        </div>
      ))}
    </div>
  );
}

function TeamCard({ name, city, score, active, color = "#005BBB", CharCmp }) {
  return (
    <motion.div
      animate={active ? { scale: 1.02, y: -2 } : { scale: 1, y: 0 }}
      className={`brutal p-4 flex items-center gap-3 ${
        active ? "" : "opacity-70"
      }`}
      style={{ background: active ? color : "#fff" }}
    >
      <div className="w-16 h-16 bg-white brutal-sm flex items-center justify-center shrink-0 overflow-hidden">
        <CharCmp size={54} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-xs font-display font-black uppercase tracking-widest ${active ? "text-white/90" : "text-neutral-500"}`}>
          {city}
        </div>
        <div className={`font-display font-black text-lg leading-tight truncate ${active ? "text-white" : "text-black"}`}>
          {name}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className={`text-[10px] font-display font-bold uppercase tracking-widest ${active ? "text-white/70" : "text-neutral-500"}`}>
          Puntos
        </div>
        <div className={`font-display font-black text-4xl leading-none ${active ? "text-canales-yellow" : "text-canales-blue"}`}>
          {score}
        </div>
      </div>
    </motion.div>
  );
}

export default function CanalesDijeronPreview() {
  const [time, setTime] = useState(12);
  useEffect(() => {
    const t = setInterval(() => setTime((v) => (v > 0 ? v - 1 : 15)), 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen bg-halftone" style={{ background: "#0d1a3f" }}>
      {/* Halftone pattern overlay */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.25) 1.5px, transparent 1.5px)",
          backgroundSize: "22px 22px",
        }}
      />
      {/* Yellow slashes */}
      <div
        className="absolute -left-40 top-24 w-[720px] h-[100px] opacity-90 pointer-events-none"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }}
      />
      <div
        className="absolute -right-40 bottom-40 w-[520px] h-[70px] opacity-80 pointer-events-none"
        style={{ background: "#FFD100", transform: "rotate(-20deg)" }}
      />

      <div className="relative max-w-6xl mx-auto p-6">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-4">
          <CanalesLogo width={140} />
          <div className="flex items-center gap-2 brutal-sm bg-canales-yellow px-3 py-1">
            <Volume2 className="w-4 h-4" />
            <span className="font-display font-black text-xs uppercase tracking-widest">
              Vivo · Ronda 3
            </span>
          </div>
        </div>

        {/* TITLE + BUBBLE */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="text-center mb-6"
        >
          <h1
            className="font-display font-black text-6xl md:text-7xl text-canales-yellow leading-none"
            style={{
              WebkitTextStroke: "3px black",
              textShadow: "6px 6px 0px #E3000F",
              transform: "rotate(-1.2deg)",
            }}
          >
            100 CANALES
          </h1>
          <h1
            className="font-display font-black text-6xl md:text-7xl text-white leading-none mt-1"
            style={{
              WebkitTextStroke: "3px black",
              textShadow: "6px 6px 0px #E3000F",
              transform: "rotate(1.2deg)",
            }}
          >
            DIJERON…
          </h1>
        </motion.div>

        {/* SPEECH BUBBLE with the question */}
        <div className="relative mb-6 flex items-start gap-3">
          <div className="hidden md:block shrink-0">
            <Carlos size={110} />
          </div>
          <div className="flex-1 brutal bg-canales-cream p-5 relative">
            <div
              className="absolute -left-3 top-8 w-6 h-6 bg-canales-cream border-l-4 border-b-4 border-black"
              style={{ transform: "rotate(45deg)" }}
            />
            <div className="text-xs font-display font-black uppercase tracking-widest text-canales-red mb-1">
              Encuestamos a 100 vendedores de Canales
            </div>
            <div className="font-display font-black text-2xl md:text-3xl leading-tight">
              "¿Cuál es la <span className="bg-canales-yellow px-2">objeción #1</span> que oyes en piso?"
            </div>
          </div>
        </div>

        {/* MAIN BOARD */}
        <div className="grid grid-cols-1 gap-2 mb-6">
          {BOARD_ANSWERS.map((a, i) => (
            <BoardSlot key={a.rank} answer={a} index={i} />
          ))}
        </div>

        {/* CONTROLS ROW */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Strikes */}
          <div className="brutal bg-white p-4">
            <div className="text-[10px] font-display font-black uppercase tracking-widest text-neutral-600 mb-2">
              Strikes del equipo
            </div>
            <StrikeIndicator count={1} />
          </div>
          {/* Timer */}
          <div className="brutal bg-canales-red text-white p-4 flex items-center gap-3">
            <Clock className="w-8 h-8" />
            <div>
              <div className="text-[10px] font-display font-black uppercase tracking-widest text-white/80">
                Turno de Alejandra L.
              </div>
              <div className="font-display font-black text-4xl leading-none">
                00:{time.toString().padStart(2, "0")}
              </div>
            </div>
          </div>
          {/* Score total */}
          <div className="brutal bg-canales-yellow p-4 flex items-center gap-3">
            <Trophy className="w-8 h-8" />
            <div>
              <div className="text-[10px] font-display font-black uppercase tracking-widest">
                Bote de la ronda
              </div>
              <div className="font-display font-black text-4xl leading-none">
                79 pts
              </div>
            </div>
          </div>
        </div>

        {/* TEAMS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <TeamCard
            name="Duncanville"
            city="CE LOPEZ"
            score={215}
            active={true}
            color="#005BBB"
            CharCmp={Carlos}
          />
          <TeamCard
            name="Fort Worth"
            city="La Gran Plaza"
            score={182}
            active={false}
            color="#E3000F"
            CharCmp={Ana}
          />
        </div>

        {/* ANSWER INPUT AREA */}
        <div className="brutal bg-white p-4 flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="flex-1 brutal-sm bg-canales-cream px-4 py-3 flex items-center gap-2">
            <Zap className="w-5 h-5 text-canales-red" />
            <span className="font-display font-bold text-lg text-neutral-500">
              Escribe tu respuesta…
            </span>
            <span className="ml-auto brutal-sm bg-canales-yellow text-black px-2 py-0.5 text-[10px] font-display font-black uppercase tracking-widest">
              ENTER
            </span>
          </div>
          <button
            className="brutal-btn bg-canales-red text-white px-6 py-3 font-display font-black text-lg inline-flex items-center gap-2 justify-center"
          >
            <Zap className="w-5 h-5" /> ¡ROBAR!
          </button>
        </div>

        {/* FOOTER */}
        <div className="mt-8 text-center text-xs font-body text-white/70">
          Mockup — así se vería el modo <b>Battle por Tiendas</b> en vivo. Los sonidos, la animación de volteo de tarjetas y el "buzzer" al fallar están simulados aquí.
        </div>
      </div>
    </div>
  );
}
